import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { localized, msg, str } from "@lit/localize";

import "@progressive-development/pd-forms/pd-suggestion-box";
import type { SuggestedElement } from "@progressive-development/pd-forms";

import { navigateOnClick } from "../../../../../common/router/app-navigation.js";
import { zahl } from "../../../../../common/format/de-format.js";
import { ROUTE_BEZIRK, ROUTE_VEEDEL } from "../../../../../config/navigation.js";
import type { GebietsAuswahl } from "../../../../../config/gebiet-auswahl.js";
import type { startseite } from "@progressive-development/politik-model";
import { blockStyles } from "../../../../../common/styles/block-styles.js";
import { eingabeStyles } from "../../../../../common/styles/eingabe-styles.js";
import {
  auswahlZeile,
  GEBIET_LOESCHEN,
  GEBIET_MERKEN,
} from "../../../../../common/components/block-parts.js";
import { AUSWAHL_GEBIETE } from "./auswahl-texte.js";

/**
 * Block 0 — Einstieg. Kein Hero, keine Überschrift, kein Nutzenversprechen:
 * die Seite beginnt mit der Gebietswahl.
 *
 * DIE SUCHE STAND FRÜHER HIER und ist bewusst weg. Sie stand über der
 * Auswahl-Zeile, die sich dadurch scheinbar auf sie bezog, und sie fragte ohne
 * jeden Zusammenhang nach einem Begriff. Ihr Platz ist der Entscheidungen-Block
 * über den beiden Listen: Dort ist der Bezug selbstverständlich, „Drucksachen"
 * wird zur Beschriftung statt zur Hürde, und es ist derselbe Weg in die Liste.
 *
 * Signature der Seite sind die neun Felder — die neun Stadtbezirke als
 * 3×3-Raster mit ihrer amtlichen Nummer. Die Nummern sind kein Dekor: Sie
 * stehen so in den Gremiennamen („Bezirksvertretung 4 (Ehrenfeld)") und in den
 * Gebietsschlüsseln.
 *
 * EIN KLICK MERKT DAS GEBIET — kein Seitenwechsel. Das Raster wird zur Leiste,
 * und die vier Blöcke darunter zeigen dieses Gebiet. Der Weg ins Veedel-Profil
 * bekommt dafür ein eigenes Bedienelement in der Leiste („Alles zu …"): Wer
 * navigieren will, soll das anklicken können, statt es beim Auswählen zu
 * bekommen. Startseite und Profil sind nicht dasselbe — die Startseite ist die
 * SCHMALE Sicht (dieselben vier Blöcke, nur enger), das Profil die TIEFE
 * (Strukturdaten im Zeitverlauf, Karte, Bezirksvertretung mit Besetzung).
 *
 * Der Merker gehört der SEITE, nicht diesem Block: Hier wird nur gemeldet, was
 * jemand wollte (`GEBIET_MERKEN`/`GEBIET_LOESCHEN`), und der neue Stand kommt
 * als Property zurück. Zwei Elemente, die beide schreiben, laufen auseinander.
 *
 * ABWEICHUNG von der pd-Components-Regel: Die neun Felder sind native
 * `<button>` (aufklappen/zuklappen, mit aria-expanded/aria-controls). pd-button
 * rendert ausschließlich Text plus Icon und kann den Aufbau des Feldes
 * (Nummer · Name · Einwohnerzahl in drei Schriftrollen) nicht tragen. Alles
 * Formseitige — Suchfeld, Absenden, Veedel-Vorschlag — kommt aus pd-forms.
 */
@customElement("app-einstieg-block")
@localized()
export class AppEinstiegBlock extends LitElement {
  /** Das Gebietsverzeichnis — alle Ebenen in einer Liste, grob nach fein. */
  @property({ attribute: false }) gebiete: startseite.GebietEintrag[] = [];

  /** Das gemerkte Gebiet — gehalten von der Seite, hier nur angezeigt. */
  @property({ attribute: false }) merker?: GebietsAuswahl;

  /** Welcher Bezirk seine Stadtteile gerade zeigt (null = keiner). */
  @state() private _offenerBezirk: string | null = null;

  static override styles = [
    blockStyles,
    eingabeStyles,
    css`
      /*
       * --- Die neun Felder ------------------------------------------------
       * Abstand zur Auswahl-Zeile: Sie ist eine Aussage ÜBER das Raster, kein
       * Teil davon — ohne Luft dazwischen liest sie sich wie seine Kopfzeile.
       */
      .raster {
        margin-top: var(--portal-space-3);
        display: grid;
        /* 3×3 auf Desktop und Tablet; unter 480 px zwei Spalten (Regel 10.1) — die Nummer bleibt die Ordnung. */
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 1px;
        background: var(--portal-linie);
        border: 1px solid var(--portal-linie);
        border-radius: var(--portal-radius);
        overflow: hidden;
      }
      .feld {
        display: flex;
        flex-direction: column;
        gap: var(--portal-space-1);
        align-items: flex-start;
        width: 100%;
        min-width: 0;
        min-height: 6.5rem;
        padding: var(--portal-space-4);
        border: none;
        border-radius: 0;
        background: var(--portal-flaeche);
        color: var(--portal-ink);
        text-align: left;
        cursor: pointer;
        font: inherit;
        transition:
          background-color 0.15s,
          color 0.15s;
      }
      .feld:hover {
        background: var(--portal-papier);
      }
      /* Aktives Gebiet — eine der drei erlaubten Akzent-Verwendungen. */
      .feld[aria-expanded="true"] {
        background: var(--portal-akzent);
        color: #fff;
      }
      .feld-nr {
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-lg);
        font-weight: 500;
        line-height: 1;
      }
      .feld-name {
        font-family: var(--portal-font-display);
        font-stretch: var(--portal-display-stretch);
        font-weight: var(--portal-display-weight);
        font-size: var(--portal-size-md);
        line-height: 1.2;
        /* Schmal muss „Rodenkirchen“ brechen dürfen, sonst schneidet das Raster die dritte Spalte ab. */
        overflow-wrap: anywhere;
        hyphens: auto;
      }
      .feld-einwohner {
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
        color: var(--portal-ink-2);
        font-variant-numeric: tabular-nums;
      }
      .feld[aria-expanded="true"] .feld-einwohner {
        color: rgba(255, 255, 255, 0.85);
      }

      /* --- Stadtteile des geöffneten Bezirks ------------------------------ */
      .stadtteile {
        margin: 0;
        padding: var(--portal-space-4) var(--portal-space-3) var(--portal-space-2);
        border: 1px solid var(--portal-linie);
        border-top: none;
        border-radius: 0 0 var(--portal-radius) var(--portal-radius);
        background: var(--portal-flaeche);
        list-style: none;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
        gap: var(--portal-space-1) var(--portal-space-4);
      }
      /* Knöpfe, keine Links — deshalb der Reset (s. _renderStadtteile). */
      .stadtteil {
        appearance: none;
        background: none;
        border: none;
        width: 100%;
        text-align: left;
        color: inherit;
        cursor: pointer;
        font: inherit;
        display: flex;
        align-items: baseline;
        gap: var(--portal-space-2);
        padding: var(--portal-space-2);
      }
      .stadtteil .nr {
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
        color: var(--portal-ink-2);
        min-width: 3ch;
      }
      .stadtteil .name {
        font-size: var(--portal-size-sm);
      }
      .stadtteil .ew {
        margin-left: auto;
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
        color: var(--portal-ink-2);
        font-variant-numeric: tabular-nums;
      }
      /*
       * --- Veedel-Vorschlagsfeld ------------------------------------------
       * Notausgang, kein zweiter Haupteingang: schmaler als das Raster, in der
       * Metaschrift beschriftet, ohne Formular-Anmutung. Es steht unter dem
       * Raster und trägt dessen Tonfall weiter.
       */
      /* Schrift und Breite kommen aus eingabeStyles — hier nur die Luft. */
      .veedel-suche {
        margin-top: var(--portal-space-4);
      }

      /* --- Zweiter Zustand: gespeichertes Gebiet -------------------------- */
      .gewaehlt {
        display: flex;
        align-items: center;
        gap: var(--portal-space-4);
        flex-wrap: wrap;
        padding: var(--portal-space-4);
        border: 1px solid var(--portal-linie);
        border-left: 3px solid var(--portal-akzent);
        border-radius: var(--portal-radius);
        background: var(--portal-flaeche);
      }
      /*
       * Die Zeile spannt über die volle Breite des Kastens: Die negativen
       * Ränder heben die Polsterung auf, damit die Haarlinie von Rahmen zu
       * Rahmen läuft. Eine eingerückte Linie sähe in einem Register aus wie
       * ein unfertiger Rand.
       */
      .gewaehlt .tiefe {
        flex-basis: 100%;
        margin: var(--portal-space-3) calc(-1 * var(--portal-space-4))
          calc(-1 * var(--portal-space-4));
        padding: var(--portal-space-3) var(--portal-space-4);
        border-top: 1px solid var(--portal-linie);
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-sm);
        color: var(--portal-ink-2);
        text-decoration: none;
      }
      .gewaehlt .tiefe:hover {
        color: var(--portal-akzent);
      }
      .gewaehlt .nr {
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-lg);
        font-weight: 500;
      }
      /* Die Ebene benennt, was der Name allein offenlässt — leiser gesetzt. */
      .gewaehlt .ebene {
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
        font-weight: 400;
        color: var(--portal-ink-2);
        text-transform: uppercase;
        letter-spacing: var(--portal-tracking-caps);
        margin-right: var(--portal-space-2);
      }
      .gewaehlt .name {
        font-family: var(--portal-font-display);
        font-stretch: var(--portal-display-stretch);
        font-weight: var(--portal-display-weight);
        font-size: var(--portal-size-lg);
      }
      .gewaehlt .aktionen {
        margin-left: auto;
        display: flex;
        gap: var(--portal-space-4);
        align-items: center;
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-sm);
      }
      .wechseln {
        background: none;
        border: none;
        padding: 0;
        font: inherit;
        color: var(--portal-ink-2);
        cursor: pointer;
        text-decoration: underline;
      }
      .wechseln:hover {
        color: var(--portal-akzent);
      }
      .aufklappen {
        display: inline-flex;
        align-items: baseline;
        gap: var(--portal-space-1);
        text-decoration: none;
      }
      .pfeil {
        display: inline-block;
        transition: transform 200ms ease;
      }
      .aufklappen[aria-expanded="true"] .pfeil {
        transform: rotate(90deg);
      }
      @media (prefers-reduced-motion: reduce) {
        .pfeil {
          transition: none;
        }
      }

      @media (max-width: 480px) {
        .raster {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        /* Neun Felder in zwei Spalten: das letzte füllt die Zeile, statt eine Lücke zu lassen. */
        .feld:nth-child(9) {
          grid-column: 1 / -1;
        }
      }
      @media (max-width: 640px) {
        .feld {
          min-height: 5rem;
          padding: var(--portal-space-3);
        }
        /* Bis 360 px bleibt das Raster 3×3 — die Einwohnerzahl weicht. */
        .feld-einwohner,
        .stadtteil .ew {
          display: none;
        }
      }
    `,
  ];

  // --- Gebietswahl ---------------------------------------------------------

  /** Auswahl über die Ebene aus den Daten, nie über die Form eines Slugs. */
  private get _stadtbezirke(): startseite.GebietEintrag[] {
    return this.gebiete.filter((g) => g.ebene === "stadtbezirk");
  }

  private get _stadtteile(): startseite.GebietEintrag[] {
    return this.gebiete.filter((g) => g.ebene === "stadtteil");
  }

  private _bezirkeNachNummer(): startseite.GebietEintrag[] {
    // Reihenfolge = amtliche Nummer; sie steht sichtbar auf jedem Feld.
    return [...this._stadtbezirke].sort((a, b) => Number(a.nr) - Number(b.nr));
  }

  /**
   * Die Stadtteile eines Bezirks.
   *
   * Der Typ verlangt einen Bezirk am Merker; Einträge ohne `bezirkSlug` wären
   * nicht merkbar. Im Bestand hat jeder Stadtteil einen — der Filter hier
   * stellt das sicher, statt später mit einem leeren Wert zu arbeiten.
   */
  private _stadtteileVon(
    bezirkSlug: string,
  ): (startseite.GebietEintrag & { bezirkSlug: string })[] {
    return this._stadtteile.filter(
      (s): s is startseite.GebietEintrag & { bezirkSlug: string } => s.bezirkSlug === bezirkSlug,
    );
  }

  /**
   * Meldet die Absicht; gespeichert wird auf der Seite.
   *
   * Wird ein BEZIRK gemerkt, klappt seine Veedel-Liste gleich auf — wer eben
   * geklickt hat, ist im Verfeinern-Modus, und ein Textknopf zwischen zwei
   * anderen wird sonst schlicht übersehen.
   *
   * NUR in diesem Moment, nicht am Zustand „Bezirk gemerkt": Der Merker
   * überlebt den Reload. Hinge die Liste an ihm, stünde sie bei jedem Besuch
   * offen — bei Porz sind das 16 Zeilen zwischen der Auswahl und dem Inhalt,
   * den man sehen wollte.
   */
  private _merke(auswahl: GebietsAuswahl): void {
    this._offenerBezirk = auswahl.ebene === "stadtbezirk" ? auswahl.slug : null;
    this.dispatchEvent(
      new CustomEvent<GebietsAuswahl>(GEBIET_MERKEN, {
        detail: auswahl,
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _wechseln(): void {
    this._offenerBezirk = null;
    this.dispatchEvent(new CustomEvent(GEBIET_LOESCHEN, { bubbles: true, composed: true }));
  }

  /** Vorschläge rein clientseitig: die 86 Stadtteile liegen ohnehin im Dokument. */
  private _veedelVorschlaege = async (
    eingabe: string,
  ): Promise<SuggestedElement<startseite.GebietEintrag>[]> => {
    const suche = eingabe.trim().toLowerCase();
    return this._stadtteile
      .filter((s) => s.name.toLowerCase().includes(suche))
      .map((s) => ({
        id: s.slug,
        name: s.name,
        subInfo: `${s.nr} · ${this._bezirkNameVon(s.bezirkSlug ?? "")}`,
        objectValue: s,
      }));
  };

  private _bezirkNameVon(slug: string): string {
    // Zuordnung über den Slug aus den Daten, nie über den Namen.
    return this._stadtbezirke.find((b) => b.slug === slug)?.name ?? slug;
  }

  private _vorschlagGewaehlt(e: CustomEvent): void {
    const stadtteil = (e.detail?.objectValue ?? e.detail) as startseite.GebietEintrag | undefined;
    // Ohne Bezirk ist der Eintrag nicht merkbar: Der Aufmacher hängt daran.
    if (!stadtteil?.slug || !stadtteil.bezirkSlug) return;
    this._merke({
      ebene: "stadtteil",
      slug: stadtteil.slug,
      name: stadtteil.name,
      nr: stadtteil.nr,
      bezirkSlug: stadtteil.bezirkSlug,
    });
  }

  // --- Render --------------------------------------------------------------

  /**
   * NUR DIE ZAHL — die Einheit steht einmal am Blockfuß.
   *
   * Sie war in allen neun Kästen dieselbe: neun Wiederholungen einer Angabe,
   * die zum Datensatz gehört und nicht zum einzelnen Bezirk. Dieselbe Bewegung,
   * die das Bezugsjahr schon gemacht hat (siehe `_renderStand`) — der Block
   * bekommt damit EINE Regel statt zweier Sonderfälle.
   *
   * Fürs Vorlesen bleibt der Wortlaut an der Zahl: Eine Sprachausgabe springt
   * von Feld zu Feld und käme sonst nie am Fußsatz vorbei.
   */
  private _renderEinwohner(g: startseite.GebietEintrag) {
    const einwohner = g.kennzahlen?.einwohner;
    if (einwohner === undefined) return nothing;
    // Fehlend ist nicht null: ohne Wert erscheint die Zeile gar nicht.
    return html`<span
      class="feld-einwohner"
      aria-label=${msg(str`${zahl(einwohner)} Einwohnerinnen und Einwohner`, {
        id: "app.start.einstieg.einwohner.vorlesen",
      })}
      >${zahl(einwohner)}</span
    >`;
  }

  /**
   * Das Bezugsjahr der Einwohnerzahlen — EINMAL am Blockfuß, nicht neunmal im
   * Raster.
   *
   * Es stand hinter jeder der neun Zahlen und war jedes Mal dieselbe Jahreszahl:
   * neun Wiederholungen einer Angabe, die zum Datensatz gehört und nicht zum
   * einzelnen Bezirk. Die Angabe verschwindet damit nicht, sie wechselt nur die
   * Ebene — dieselbe Form, in der auch eine Auswahl-Begründung an den Blockfuß
   * wandert.
   *
   * Nur wenn ALLE gezeigten Zahlen dasselbe Jahr tragen. Lieferte die Pipeline
   * je Bezirk ein anderes, wäre ein gemeinsamer Satz falsch — dann steht hier
   * nichts, und das Jahr steht weiter an der Zahl auf der Gebietsseite.
   */
  private _renderStand() {
    const jahre = new Set(
      this._bezirkeNachNummer()
        .filter((b) => b.kennzahlen?.einwohner !== undefined)
        .map((b) => b.kennzahlen?.jahr)
        .filter((j): j is number => j !== undefined),
    );
    // Die EINHEIT steht immer, das Jahr nur, wenn es für alle dasselbe ist.
    // Ohne diese Zeile trüge keine der neun Zahlen mehr eine Einheit.
    const jahr = jahre.size === 1 ? [...jahre][0] : undefined;
    return html`<p class="hinweis">
      ${
        jahr !== undefined
          ? msg(str`Einwohnerinnen und Einwohner, Stand ${jahr}.`, {
              id: "app.start.einstieg.stand",
            })
          : msg("Einwohnerinnen und Einwohner.", { id: "app.start.einstieg.stand.ohneJahr" })
      }
    </p>`;
  }

  private _renderRaster() {
    const bezirke = this._bezirkeNachNummer();
    return html`
      <div
        class="raster"
        role="group"
        aria-label=${msg("Stadtbezirke", { id: "app.start.einstieg.raster" })}
      >
        ${bezirke.map(
          (b) => html`
            <button
              type="button"
              class="feld"
              @click=${() =>
                this._merke({
                  ebene: "stadtbezirk",
                  slug: b.slug,
                  name: b.name,
                  nr: b.nr,
                  bezirkSlug: b.slug,
                })}
            >
              <span class="feld-nr">${b.nr}</span>
              <span class="feld-name">${b.name}</span>
              ${this._renderEinwohner(b)}
            </button>
          `,
        )}
      </div>
    `;
  }

  /**
   * Die Stadtteile eines Bezirks — nur zum Verfeinern.
   *
   * KEINE Zeile „Ganzer Bezirk" mehr: Die Liste erscheint erst, wenn der
   * Bezirk gemerkt ist (der Klick aufs Rasterfeld tut das). Sie böte also an,
   * zu wählen, was schon gewählt ist.
   */
  private _renderStadtteile(bezirkSlug: string) {
    const stadtteile = this._stadtteileVon(bezirkSlug);
    if (stadtteile.length === 0) return nothing;
    return html`
      <ul id="stadtteile" class="stadtteile">
        ${stadtteile.map(
          (s) => html`
            <li>
              <button
                type="button"
                class="stadtteil"
                @click=${() =>
                  this._merke({
                    ebene: "stadtteil",
                    slug: s.slug,
                    name: s.name,
                    nr: s.nr,
                    bezirkSlug: s.bezirkSlug,
                  })}
              >
                <span class="nr">${s.nr}</span>
                <span class="name">${s.name}</span>
                ${
                  s.kennzahlen?.einwohner !== undefined
                    ? html`<span class="ew">${zahl(s.kennzahlen.einwohner)}</span>`
                    : nothing
                }
              </button>
            </li>
          `,
        )}
      </ul>
    `;
  }

  /**
   * Das gemerkte Gebiet.
   *
   * Die Ebene steht dabei, weil der Name allein zweideutig ist: ACHT der neun
   * Stadtbezirke haben einen gleichnamigen Stadtteil (nur Innenstadt nicht).
   * „Ehrenfeld" kann also der Bezirk mit 112.000 Einwohnern sein oder der
   * Stadtteil mit 37.000. In den Daten ist die Auswahl eindeutig — sie trägt
   * ihre Ebene mit —, aber angezeigt wäre sie es nicht.
   */
  private _renderGewaehlt(gebiet: GebietsAuswahl) {
    const ebene =
      gebiet.ebene === "stadtbezirk"
        ? msg("Stadtbezirk", { id: "app.start.einstieg.ebene.bezirk" })
        : msg("Stadtteil", { id: "app.start.einstieg.ebene.stadtteil" });
    /*
     * Das Ziel folgt der EBENE, nicht dem Slug: Bezirke und Stadtteile haben
     * getrennte Adressräume, weil acht der neun Bezirks-Slugs zugleich
     * Stadtteil-Slugs sind. Ein fester Pfad landete bei einem gemerkten
     * Stadtteil auf der falschen Seite — und zwar lautlos, weil es die
     * gleichnamige Bezirksseite gibt.
     */
    const ziel =
      gebiet.ebene === "stadtbezirk"
        ? `/${ROUTE_BEZIRK}/${gebiet.slug}`
        : `/${ROUTE_VEEDEL}/${gebiet.slug}`;
    return html`
      <div class="gewaehlt">
        <span class="nr">${gebiet.nr}</span>
        <span class="name"><span class="ebene">${ebene}</span> ${gebiet.name}</span>
        <span class="aktionen">
          <!--
            Aus einem Bezirk heraus lässt sich enger wählen, ohne den Merker
            erst wegzuwerfen. Bei einem Stadtteil gibt es nichts Engeres.
          -->
          ${
            gebiet.ebene === "stadtbezirk"
              ? html`<button
                  type="button"
                  class="wechseln aufklappen"
                  aria-expanded=${this._offenerBezirk === gebiet.slug ? "true" : "false"}
                  aria-controls="stadtteile"
                  @click=${() =>
                    (this._offenerBezirk =
                      this._offenerBezirk === gebiet.slug ? null : gebiet.slug)}
                >
                  <!--
                  Pfeil wie in pd-history (PdHistoryItem): ▸, das beim Öffnen
                  um 90 Grad kippt. Dort ist es bereits Bibliotheksform — und
                  dieser Knopf soll später selbst eine Komponente werden.

                  ACHTUNG, zwei Formen im Haus: app-beratungsweg.ts nutzt ⌄ mit
                  180 Grad. Beim Herauslösen der Komponente gehört das
                  vereinheitlicht, sonst hat die App zwei Aufklapp-Vokabulare.
                -->
                  <span class="pfeil" aria-hidden="true">▸</span>
                  ${msg("Veedel wählen", { id: "app.start.einstieg.veedelWaehlen" })}
                </button>`
              : nothing
          }
          <!--
            „wechseln" allein ließ offen, was wechselt. „Gebiet wechseln" nennt
            den Gegenstand und bleibt kurz genug fürs Telefon.

            Die Handlung ist dieselbe wie „Ganz Köln zeigen" am Blockfuß — der
            Merker wird verworfen. Die Beschriftungen unterscheiden sich, weil
            die Absicht sich unterscheidet: Hier erscheint danach das Raster
            zum Neuwählen, dort will jemand die ganze Stadt sehen. Gleiche
            Mechanik, zwei ehrliche Wege.
          -->
          <button type="button" class="wechseln" @click=${this._wechseln}>
            ${msg("Gebiet wechseln", { id: "app.start.einstieg.gebietWechseln" })}
          </button>
        </span>
        <!--
          DER WEG IN DIE TIEFE — als eigene Zeile IM Merker, unter einer
          Haarlinie.

          Er stand einmal zwischen den Knöpfen und sah aus wie ihr Geschwister,
          obwohl er als einziger die Seite verlässt; danach schwebte er
          darunter im Weißraum und wirkte wie ein Nachsatz. Beides ist hier
          gelöst: eigene Zeile, durch eine Linie getrennt — aber Teil des
          Objekts, das der Nutzer gerade erzeugt hat.

          DER SATZ NENNT, WOFÜR SICH DER KLICK LOHNT. „Alles zu Niehl" hatte
          keinen Informationsduft; im ersten Test wurde der Link übersehen.
          Genannt werden nur Angaben, die es für JEDES Gebiet gibt — deshalb
          ein fester Satz und nichts je Gebiet Zusammengesetztes. Drucksachen
          fehlen bewusst: Sie liegen nicht überall vor. Aufgezählt, nicht
          bewertet — kein „alles Wichtige", das wäre eine Auswahlbehauptung.

          Hier fällt das Wort „Bodenrichtwerte" auf der Startseite zum ersten
          Mal: der günstigste Einstieg in den Datensatz, der jeden unmittelbar
          betrifft — keine neue Fläche, keine neue Pflege, ein Wort an der
          Stelle, an der ohnehin gerade eine Auswahl getroffen wurde.
        -->
        <a class="tiefe" href=${ziel} @click=${(e: Event) => navigateOnClick(e, ziel)}
          >${msg(str`Wahlergebnisse, Strukturdaten und Bodenrichtwerte für ${gebiet.name}`, {
            id: "app.start.einstieg.tiefe",
          })}
          →</a
        >
      </div>
      ${this._offenerBezirk ? this._renderStadtteile(this._offenerBezirk) : nothing}
    `;
  }

  override render() {
    return html`
      ${auswahlZeile(AUSWAHL_GEBIETE())}
      ${
        this.merker
          ? this._renderGewaehlt(this.merker)
          : html`${this._renderRaster()}${this._renderVeedelSuche()}${this._renderStand()}`
      }
    `;
  }

  /**
   * Der Notausgang aus dem Raster — NUR im Raster-Zustand.
   *
   * Er ist kein zweiter Haupteingang, sondern die Rettung für alle, die ihr
   * Veedel kennen, aber nicht seinen Bezirk: NUR 8 der 86 Stadtteile tragen
   * den Namen ihres Bezirks. Für die anderen 78 gibt der Name keinen Hinweis
   * darauf, wo im Raster zu klicken wäre — Sülz liegt in Lindenthal, Vingst in
   * Kalk. Ohne dieses Feld hieße das: bis zu neun Bezirke durchklicken.
   *
   * Im gemerkten Zustand ist es weg. Dort zeigt die Leiste die Antwort und
   * „wechseln" führt zurück ins Raster — ein Suchfeld daneben wäre Rauschen.
   */
  private _renderVeedelSuche() {
    return html`
      <div class="veedel-suche">
        <pd-suggestion-box
          label=${msg("Veedel suchen", { id: "app.start.einstieg.veedelSuche" })}
          .callFunction=${this._veedelVorschlaege}
          .minCharsToSearch=${1}
          .maxResult=${8}
          @element-selected=${this._vorschlagGewaehlt}
        ></pd-suggestion-box>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-einstieg-block": AppEinstiegBlock;
  }
}
